type ScrollPropertyGridCellIntoViewOptions = {
  behavior?: ScrollBehavior;
  padding?: number;
};

const findScrollParent = (element: HTMLElement): HTMLElement | null => {
  let parent = element.parentElement;
  while (parent) {
    const { overflow, overflowX, overflowY } = getComputedStyle(parent);
    if (/(auto|scroll)/.test(`${overflow}${overflowX}${overflowY}`)) {
      return parent;
    }
    parent = parent.parentElement;
  }
  return null;
};

const getStickyInset = (
  scrollContainer: HTMLElement,
  axis: 'left' | 'top',
): number => {
  const containerRect = scrollContainer.getBoundingClientRect();
  const edgeProp = axis === 'left' ? 'left' : 'top';
  const sizeProp = axis === 'left' ? 'right' : 'bottom';
  const containerEdge = containerRect[edgeProp];
  let inset = 0;

  for (const element of scrollContainer.querySelectorAll<HTMLElement>('*')) {
    const style = getComputedStyle(element);
    if (style.position !== 'sticky') continue;

    const offset = style[edgeProp];
    if (offset === 'auto') continue;

    const rect = element.getBoundingClientRect();
    const stuckEdge = containerEdge + Number.parseFloat(offset);
    if (Math.abs(rect[edgeProp] - stuckEdge) > 2) continue;

    const overlaySize = rect[sizeProp] - containerEdge;
    inset = Math.max(inset, overlaySize);
  }

  return inset;
};

export const scrollPropertyGridCellIntoView = (
  element: HTMLElement,
  { behavior = 'smooth', padding = 8 }: ScrollPropertyGridCellIntoViewOptions = {},
) => {
  const scrollContainer = findScrollParent(element);
  if (!scrollContainer) {
    element.scrollIntoView({ behavior, block: 'center', inline: 'center' });
    return;
  }

  const containerRect = scrollContainer.getBoundingClientRect();
  const elementRect = element.getBoundingClientRect();
  const stickyLeft = getStickyInset(scrollContainer, 'left');
  const stickyTop = getStickyInset(scrollContainer, 'top');

  let { scrollLeft, scrollTop } = scrollContainer;

  const elementLeftInView = elementRect.left - containerRect.left;
  const elementRightInView = elementRect.right - containerRect.left;
  const minLeft = stickyLeft + padding;
  const maxRight = containerRect.width - padding;

  if (elementLeftInView < minLeft) {
    scrollLeft += elementLeftInView - minLeft;
  } else if (elementRightInView > maxRight) {
    scrollLeft += elementRightInView - maxRight;
  }

  const elementTopInView = elementRect.top - containerRect.top;
  const elementBottomInView = elementRect.bottom - containerRect.top;
  const minTop = stickyTop + padding;
  const maxBottom = containerRect.height - padding;

  if (elementTopInView < minTop) {
    scrollTop += elementTopInView - minTop;
  } else if (elementBottomInView > maxBottom) {
    scrollTop += elementBottomInView - maxBottom;
  }

  scrollLeft = Math.max(0, scrollLeft);
  scrollTop = Math.max(0, scrollTop);

  if (typeof scrollContainer.scrollTo === 'function') {
    scrollContainer.scrollTo({ left: scrollLeft, top: scrollTop, behavior });
  } else {
    scrollContainer.scrollLeft = scrollLeft;
    scrollContainer.scrollTop = scrollTop;
  }
};
